/** राशियों की जानकारी, chart rows for Jyotish → Planets info tab */

export const RASHI_CHART_COLUMNS = [
  { key: 'rashi', label: 'राशी', labelEn: 'Sign' },
  { key: 'aakriti', label: 'आकृति', labelEn: 'Form / symbol' },
  { key: 'rang', label: 'रंग', labelEn: 'Colour' },
  { key: 'tatva', label: 'तत्व', labelEn: 'Element' },
  { key: 'varn', label: 'वर्ण', labelEn: 'Varna' },
  { key: 'disha', label: 'दिशाएँ', labelEn: 'Direction' },
  { key: 'samVisham', label: 'सम/विषम', labelEn: 'Even / odd' },
  { key: 'swabhav', label: 'स्वभाव', labelEn: 'Nature' },
  { key: 'gun', label: 'गुण/वृत्ति', labelEn: 'Guna' },
  { key: 'prakriti', label: 'प्रकृति (वात/कफ/पित्त)', labelEn: 'Dosha' },
  { key: 'swami', label: 'राशि स्वामी', labelEn: 'Sign lord' },
];

/** Attribute keys shown on the planets-info card body (lord is in the header). */
export const RASHI_CHART_ATTRIBUTE_KEYS = [
  'aakriti',
  'rang',
  'tatva',
  'varn',
  'disha',
  'samVisham',
  'swabhav',
  'gun',
  'prakriti',
];

const SWABHAV_EN = {
  चर: 'Movable (Chara)',
  स्थिर: 'Fixed (Sthira)',
  द्विस्वभाव: 'Dual (Dwiswabhava)',
};

const SAM_VISHAM_EN = {
  विषम: 'Odd',
  सम: 'Even',
};

const TATVA_EN = {
  अग्नि: 'Fire',
  पृथ्वी: 'Earth',
  वायु: 'Air',
  जल: 'Water',
};

const GUN_EN = {
  सात्विक: 'Sattva',
  राजसिक: 'Rajas',
  तामसिक: 'Tamas',
};

const VARN_EN = {
  क्षत्रीय: 'Kshatriya',
  वैश्य: 'Vaishya',
  शूद्र: 'Shudra',
  ब्राह्मण: 'Brahmin',
};

const DISHA_EN = {
  पूर्व: 'East',
  दक्षिण: 'South',
  पश्चिम: 'West',
  उत्तर: 'North',
};

const PRAKRITI_EN = {
  पित्त: 'Pitta',
  कफ: 'Kapha',
  वात: 'Vata',
  त्रिदोष: 'Tridosha',
};

/** @param {string} key */
export function getRashiChartFieldLabel(key, showEnglish) {
  const col = RASHI_CHART_COLUMNS.find((c) => c.key === key);
  if (!col) return key;
  return showEnglish ? col.labelEn : col.label;
}

/**
 * Plain-text display value for a rashi chart field (tatva handled separately in UI).
 * @param {Record<string, string | number>} row
 * @param {string} key
 * @param {boolean} showEnglish
 */
export function getRashiChartFieldValue(row, key, showEnglish) {
  const raw = row[key];
  if (raw == null) return '';
  if (!showEnglish) return raw;

  switch (key) {
    case 'tatva':
      return TATVA_EN[raw] ?? raw;
    case 'swabhav':
      return SWABHAV_EN[raw] ?? raw;
    case 'samVisham':
      return SAM_VISHAM_EN[raw] ?? raw;
    case 'gun':
      return GUN_EN[raw] ?? raw;
    case 'varn':
      return VARN_EN[raw] ?? raw;
    case 'disha':
      return DISHA_EN[raw] ?? raw;
    case 'prakriti':
      return PRAKRITI_EN[raw] ?? raw;
    default:
      return raw;
  }
}

/** English names for subtitles (order matches `num` 1-12). */
export const RASHI_CHART_ROW_ENGLISH = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
];

/** IAST-style names (order matches `num` 1-12). */
export const RASHI_CHART_ROW_LATIN = [
  'Mesha',
  'Vrishabha',
  'Mithuna',
  'Karka',
  'Simha',
  'Kanya',
  'Tula',
  'Vrishchika',
  'Dhanu',
  'Makara',
  'Kumbha',
  'Meena',
];

export const RASHI_CHART_ROWS = [
  {
    num: 1,
    rashi: 'मेष',
    aakriti: 'भेड़ (मेढ़ा)',
    rang: 'लाल',
    tatva: 'अग्नि',
    varn: 'क्षत्रीय',
    disha: 'पूर्व',
    samVisham: 'विषम',
    swabhav: 'चर',
    gun: 'तामसिक',
    prakriti: 'पित्त',
    swami: 'मंगल',
  },
  {
    num: 2,
    rashi: 'वृषभ',
    aakriti: 'बैल',
    rang: 'सफेद',
    tatva: 'पृथ्वी',
    varn: 'वैश्य',
    disha: 'दक्षिण',
    samVisham: 'सम',
    swabhav: 'स्थिर',
    gun: 'राजसिक',
    prakriti: 'त्रिदोष',
    swami: 'शुक्र',
  },
  {
    num: 3,
    rashi: 'मिथुन',
    aakriti: 'युवा जोड़ा',
    rang: 'हरा',
    tatva: 'वायु',
    varn: 'शूद्र',
    disha: 'पश्चिम',
    samVisham: 'विषम',
    swabhav: 'द्विस्वभाव',
    gun: 'राजसिक',
    prakriti: 'वात',
    swami: 'बुध',
  },
  {
    num: 4,
    rashi: 'कर्क',
    aakriti: 'केंकड़ा',
    rang: 'गुलाबी',
    tatva: 'जल',
    varn: 'ब्राह्मण',
    disha: 'उत्तर',
    samVisham: 'सम',
    swabhav: 'चर',
    gun: 'सात्विक',
    prakriti: 'कफ',
    swami: 'चंद्रमा',
  },
  {
    num: 5,
    rashi: 'सिंह',
    aakriti: 'शेर',
    rang: 'श्वेताभ सुनहारा',
    tatva: 'अग्नि',
    varn: 'क्षत्रीय',
    disha: 'पूर्व',
    samVisham: 'विषम',
    swabhav: 'स्थिर',
    gun: 'सात्विक',
    prakriti: 'पित्त',
    swami: 'सूर्य',
  },
  {
    num: 6,
    rashi: 'कन्या',
    aakriti: 'कुँवारी कन्या',
    rang: 'रंगबिरंगा',
    tatva: 'पृथ्वी',
    varn: 'वैश्य',
    disha: 'दक्षिण',
    samVisham: 'सम',
    swabhav: 'द्विस्वभाव',
    gun: 'राजसिक',
    prakriti: 'त्रिदोष',
    swami: 'बुध',
  },
  {
    num: 7,
    rashi: 'तुला',
    aakriti: 'तराजू',
    rang: 'काला',
    tatva: 'वायु',
    varn: 'शूद्र',
    disha: 'पश्चिम',
    samVisham: 'विषम',
    swabhav: 'चर',
    gun: 'राजसिक',
    prakriti: 'वात',
    swami: 'शुक्र',
  },
  {
    num: 8,
    rashi: 'वृश्चिक',
    aakriti: 'बिच्छू',
    rang: 'केसरी',
    tatva: 'जल',
    varn: 'ब्राह्मण',
    disha: 'उत्तर',
    samVisham: 'सम',
    swabhav: 'स्थिर',
    gun: 'तामसिक',
    prakriti: 'कफ',
    swami: 'मंगल',
  },
  {
    num: 9,
    rashi: 'धनु',
    aakriti: 'धनुर्धर',
    rang: 'सुनहारा',
    tatva: 'अग्नि',
    varn: 'क्षत्रीय',
    disha: 'पूर्व',
    samVisham: 'विषम',
    swabhav: 'द्विस्वभाव',
    gun: 'सात्विक',
    prakriti: 'पित्त',
    swami: 'गुरु',
  },
  {
    num: 10,
    rashi: 'मकर',
    aakriti: 'मृग/हाथी',
    rang: 'विविध रंग',
    tatva: 'पृथ्वी',
    varn: 'वैश्य',
    disha: 'दक्षिण',
    samVisham: 'सम',
    swabhav: 'चर',
    gun: 'तामसिक',
    prakriti: 'त्रिदोष',
    swami: 'शनि',
  },
  {
    num: 11,
    rashi: 'कुंभ',
    aakriti: 'घड़ा',
    rang: 'भूरा',
    tatva: 'वायु',
    varn: 'शूद्र',
    disha: 'पश्चिम',
    samVisham: 'विषम',
    swabhav: 'स्थिर',
    gun: 'तामसिक',
    prakriti: 'वात',
    swami: 'शनि',
  },
  {
    num: 12,
    rashi: 'मीन',
    aakriti: 'दो विपरीत मछलियाँ',
    rang: 'सफेद',
    tatva: 'जल',
    varn: 'ब्राह्मण',
    disha: 'उत्तर',
    samVisham: 'सम',
    swabhav: 'द्विस्वभाव',
    gun: 'सात्विक',
    prakriti: 'कफ',
    swami: 'गुरु',
  },
];

/** Classical seven in natural teaching order (Sun → Saturn). */
export const SIGN_LORD_SWAMI_ORDER = ['सूर्य', 'चंद्रमा', 'मंगल', 'बुध', 'गुरु', 'शुक्र', 'शनि'];

/**
 * Group chart rows by natural sign lord for the planets-info page.
 * @returns {Array<{ swamiHi: string; signs: typeof RASHI_CHART_ROWS }>}
 */
export function buildSignLordGroups() {
  return SIGN_LORD_SWAMI_ORDER.map((swamiHi) => ({
    swamiHi,
    signs: RASHI_CHART_ROWS.filter((row) => row.swami === swamiHi),
  })).filter((group) => group.signs.length > 0);
}
